import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  Shield, User, Hash, TrendingUp, Award, Search,
  Download, Loader2, AlertCircle, GraduationCap, LogOut,
  ChevronUp, ChevronDown, Users, FileUp, FileDown, X,
  ListOrdered, Star, ArrowUp, ArrowDown, BookOpen, Save,
  Trash2, Plus, Check, RefreshCw, Bookmark, ChevronLeft,
  ChevronRight, BarChart2, MoveVertical, ChevronsUp, ChevronsDown,
  Sparkles, CheckCircle2
} from "lucide-react";

/* ═══════════════════════════════════════════════════════
   CONSTANTS & TIER CONFIGURATION
═══════════════════════════════════════════════════════ */
const MAHA_CATEGORIES = ["OPEN", "OBC", "SC", "ST", "EWS", "VJA", "NTB", "NTC", "NTD", "SEBC"];
const SESSION_KEY = "ame_verified_student";

const FOUR_TIER_COLORS = {
  Safe:     { bg: "rgba(16,185,129,0.15)",  border: "rgba(16,185,129,0.4)",  text: "#10b981" },
  Possible: { bg: "rgba(59,130,246,0.15)",  border: "rgba(59,130,246,0.4)",  text: "#3b82f6" },
  Target:   { bg: "rgba(245,158,11,0.15)",  border: "rgba(245,158,11,0.4)",  text: "#f59e0b" },
  Dream:    { bg: "rgba(244,63,94,0.15)",   border: "rgba(244,63,94,0.4)",   text: "#f43f5e" },
};

const TIER_ACCENT = {
  Safe:     "#10b981",
  Possible: "#3b82f6",
  Target:   "#f59e0b",
  Dream:    "#f43f5e",
};

function getTier(marginPercent) {
  if (marginPercent == null) return "Dream";
  if (marginPercent < -8)  return "Dream";
  if (marginPercent <= 8)  return "Target";
  if (marginPercent <= 25) return "Possible";
  return "Safe";
}

/* ═══════════════════════════════════════════════════════
   COLLEGE CARD (Predicted List Item)
═══════════════════════════════════════════════════════ */
function CollegeCard({ p, index, onAddPriority, isInPriority }) {
  const isNew   = !!p.is_new_college || p.cutoff_rank == null;
  const tier    = isNew ? null : (p.custom_tier || getTier(p.margin_percent));
  const tc      = tier ? (FOUR_TIER_COLORS[tier] || FOUR_TIER_COLORS.Dream) : null;
  const accent  = isNew ? "#f59e0b" : (TIER_ACCENT[tier] || "#f43f5e");
  const isStray = p.expected_rounds && p.expected_rounds.some(r => r.round === "R4");
  const seqNum  = p.priority_order != null ? p.priority_order : (index + 1);

  const isGovt = (p.status || "").toLowerCase().includes("govt");
  const isCorp = (p.status || "").toLowerCase().includes("corp");

  const smlMatch = p.all_matches?.["SML"];
  const catMatch = p.all_matches?.["CAT-RANK"];
  const hasBoth  = smlMatch && catMatch;
  const bestType = p.matched_rank_type || "SML";

  return (
    <div style={{
      display: "flex", gap: 0,
      background: "rgba(255,255,255,0.025)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderLeft: `3px solid ${accent}`,
      borderRadius: 9, marginBottom: 7, overflow: "hidden",
      transition: "background 0.15s",
    }}
    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.045)"}
    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.025)"}
    >
      {/* Serial Number */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        minWidth: 40, padding: "0 4px",
        borderRight: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(0,0,0,0.15)"
      }}>
        <span style={{ fontSize: "0.74rem", fontWeight: 800, color: "#38bdf8" }}>#{seqNum}</span>
      </div>

      {/* College Info */}
      <div style={{ flex: 1, padding: "10px 12px", minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#f1f5f9", lineHeight: 1.35, wordBreak: "break-word" }}>
            {p.college_name}
          </div>
          {onAddPriority && (
            <button
              onClick={() => onAddPriority(p)}
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 9px",
                borderRadius: 6,
                fontSize: "0.68rem",
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "inherit",
                border: isInPriority ? "1px solid rgba(16,185,129,0.5)" : "1px solid rgba(139,92,246,0.5)",
                background: isInPriority ? "rgba(16,185,129,0.18)" : "rgba(139,92,246,0.18)",
                color: isInPriority ? "#34d399" : "#c4b5fd",
                transition: "all 0.15s"
              }}
              title={isInPriority ? "College is in your priority list" : "Add college to your priority list"}
            >
              {isInPriority ? <><Check size={12} /> Added</> : <><Plus size={12} /> Add to Priority</>}
            </button>
          )}
        </div>

        <div style={{ fontSize: "0.62rem", color: "#64748b", marginTop: 2 }}>
          {[p.college_code && p.college_code !== "NEW" ? `Choice Code: ${p.college_code}` : null, p.status ? p.status.toUpperCase() : null, "Maharashtra"].filter(Boolean).join(" · ")}
        </div>

        {/* Badges row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6, alignItems: "center" }}>
          {!isNew && tier && (
            <span style={{
              fontSize: "0.6rem", padding: "2px 8px", borderRadius: 4, fontWeight: 800,
              background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text,
              textTransform: "uppercase", letterSpacing: "0.6px",
            }}>{tier}</span>
          )}

          {isNew && (
            <span style={{
              fontSize: "0.6rem", padding: "2px 8px", borderRadius: 4, fontWeight: 800,
              background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)",
              color: "#f59e0b", letterSpacing: "0.4px",
            }}>NEW COLLEGE (2026-27)</span>
          )}

          {!isNew && p.expected_rounds && p.expected_rounds.map((er, i) => (
            <span key={i} style={{
              fontSize: "0.55rem", padding: "1px 5px", borderRadius: 3, fontWeight: 700,
              background: (isStray && er.round === "R4") ? "rgba(239,68,68,0.12)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${(isStray && er.round === "R4") ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.1)"}`,
              color: (isStray && er.round === "R4") ? "#ef4444" : "#94a3b8",
            }}>
              {er.round}{(isStray && er.round === "R4") ? " ⚠️" : ""}
            </span>
          ))}

          {p.status && (
            <span style={{
              fontSize: "0.55rem", padding: "1px 6px", borderRadius: 3, fontWeight: 700,
              background: isGovt ? "rgba(16,185,129,0.12)" : isCorp ? "rgba(56,189,248,0.12)" : "rgba(245,158,11,0.12)",
              color: isGovt ? "#10b981" : isCorp ? "#38bdf8" : "#f59e0b",
              border: `1px solid ${isGovt ? "rgba(16,185,129,0.25)" : isCorp ? "rgba(56,189,248,0.25)" : "rgba(245,158,11,0.25)"}`,
              textTransform: "uppercase",
            }}>{p.status}</span>
          )}

          {p.fees && p.fees !== "—" && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 2 }}>
              <span style={{ fontSize: "0.64rem", color: "#c084fc", fontWeight: 700 }}>
                {String(p.fees).startsWith("₹") ? p.fees : `₹ ${p.fees}`}
              </span>
              <span style={{ fontSize: "0.54rem", color: "#94a3b8", fontWeight: 500 }}>
                (*Prev. Year Fee · Check 2026-27)
              </span>
            </div>
          )}

          {(p.college_name || "").includes("No Fee Benefits") && (
            <span style={{
              fontSize: "0.55rem", padding: "1px 6px", borderRadius: 3, fontWeight: 700,
              background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
              color: "#f87171",
            }}>⚠️ No Fee Benefits</span>
          )}
        </div>
      </div>

      {/* Rank Match Column */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center",
        padding: "10px 14px",
        borderLeft: "1px solid rgba(255,255,255,0.06)",
        minWidth: 145, textAlign: "right",
      }}>
        {isNew ? (
          <>
            <span style={{ fontSize: "0.5rem", color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 800, marginBottom: 3 }}>
              NEW SEATS
            </span>
            <div style={{ fontSize: "0.68rem", color: "#fbbf24", fontWeight: 800 }}>Fresh Seat Pool</div>
            <div style={{ fontSize: "0.55rem", color: "#64748b", marginTop: 2 }}>No Prior Cutoff</div>
          </>
        ) : hasBoth ? (
          <>
            <span style={{ fontSize: "0.52rem", color: "#fbbf24", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 800, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 3 }}>
              ⭐ Best: {bestType === "CAT-RANK" ? "Cat-Rank" : "SML"}
            </span>

            <div style={{ fontSize: "0.64rem", color: bestType === "CAT-RANK" ? "#f1f5f9" : "#94a3b8", fontWeight: bestType === "CAT-RANK" ? 800 : 600, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, lineHeight: 1.3 }}>
              <span style={{ fontSize: "0.52rem", color: bestType === "CAT-RANK" ? "#f59e0b" : "#64748b", fontWeight: 700 }}>
                {catMatch.matched_category || "CAT"}:
              </span>
              <span>{catMatch.student_rank?.toLocaleString("en-IN")}</span>
              <span style={{ color: "#475569" }}>/</span>
              <span>{catMatch.cutoff_rank?.toLocaleString("en-IN")}</span>
              {catMatch.cutoff_rank && (
                <span style={{ fontSize: "0.58rem", fontWeight: 800, color: (catMatch.student_rank - catMatch.cutoff_rank <= 0) ? "#10b981" : "#ef4444", marginLeft: 2 }}>
                  {catMatch.student_rank - catMatch.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(catMatch.student_rank - catMatch.cutoff_rank)).toLocaleString("en-IN")}
                </span>
              )}
            </div>

            <div style={{ fontSize: "0.64rem", color: bestType === "SML" ? "#f1f5f9" : "#64748b", fontWeight: bestType === "SML" ? 800 : 500, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, lineHeight: 1.3, marginTop: 2 }}>
              <span style={{ fontSize: "0.52rem", color: bestType === "SML" ? "#38bdf8" : "#475569", fontWeight: 700 }}>
                SML:
              </span>
              <span>{smlMatch.student_rank?.toLocaleString("en-IN")}</span>
              <span style={{ color: "#334155" }}>/</span>
              <span>{smlMatch.cutoff_rank?.toLocaleString("en-IN")}</span>
              {smlMatch.cutoff_rank && (
                <span style={{ fontSize: "0.58rem", fontWeight: 700, color: (smlMatch.student_rank - smlMatch.cutoff_rank <= 0) ? "#10b981" : "#ef4444", marginLeft: 2 }}>
                  {smlMatch.student_rank - smlMatch.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(smlMatch.student_rank - smlMatch.cutoff_rank)).toLocaleString("en-IN")}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <span style={{ fontSize: "0.5rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 700, marginBottom: 3 }}>
              {p.matched_rank_type || "SML"} MATCH
            </span>
            <div style={{ fontSize: "0.74rem", fontWeight: 700, color: "#f1f5f9" }}>
              {p.student_rank ? p.student_rank.toLocaleString("en-IN") : "—"}
              <span style={{ color: "#475569", fontWeight: 400, margin: "0 3px" }}>/</span>
              {p.cutoff_rank  ? p.cutoff_rank.toLocaleString("en-IN")  : "—"}
            </div>
            {p.student_rank && p.cutoff_rank && (
              <div style={{ fontSize: "0.64rem", fontWeight: 800, color: (p.student_rank - p.cutoff_rank <= 0) ? "#10b981" : "#ef4444", marginTop: 2 }}>
                ({p.student_rank - p.cutoff_rank <= 0 ? "-" : "+"}{Math.abs(Math.round(p.student_rank - p.cutoff_rank)).toLocaleString("en-IN")})
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   VERIFICATION GATE
═══════════════════════════════════════════════════════ */
export function VerificationGate({ onVerified }) {
  const [step, setStep] = useState("LOGIN");
  const [regNo, setRegNo] = useState("");
  const [air, setAir] = useState("");
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [sml, setSml] = useState("");
  const [category, setCategory] = useState("OPEN");
  const [categoryRank, setCategoryRank] = useState("");
  const [gender, setGender] = useState("Male");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInitialVerify = async (e) => {
    e?.preventDefault();
    setError("");
    const trimmedReg = regNo.trim().toUpperCase();
    const airNum = parseInt(air, 10);
    if (!trimmedReg) { setError("Please enter your Receipt / Registration Number."); return; }
    if (!air || isNaN(airNum) || airNum <= 0) { setError("Please enter a valid All India Rank (AIR)."); return; }

    setLoading(true);
    try {
      const res = await api.post("/medical-predictor/verify-student", { reg_no: trimmedReg, air: airNum });
      const data = res.data;
      if (data.is_locked) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
        onVerified(data);
      } else {
        setCandidateInfo(data);
        setSml(data.sml ? String(data.sml) : "");
        setCategory(data.category || "OPEN");
        setCategoryRank(data.category_rank ? String(data.category_rank) : "");
        setGender(data.gender || "Male");
        setStep("LOCK_SML");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Student record not found. Please check your details.");
    } finally { setLoading(false); }
  };

  const handleLockSmlSubmit = async (e) => {
    e?.preventDefault();
    setError("");
    const smlNum = parseInt(sml, 10);
    if (!sml || isNaN(smlNum) || smlNum <= 0) { setError("Please enter a valid State Merit List rank (SML)."); return; }
    if (category !== "OPEN") {
      const catRankNum = parseInt(categoryRank, 10);
      if (!categoryRank || isNaN(catRankNum) || catRankNum <= 0) {
        setError(`Please enter your ${category} Category Rank.`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        reg_no: candidateInfo.reg_no,
        air: candidateInfo.air,
        sml: smlNum,
        category,
        category_rank: category !== "OPEN" ? parseInt(categoryRank, 10) : null,
        gender,
      };
      const res = await api.post("/medical-predictor/lock-student-sml", payload);
      const studentObj = res.data?.student || res.data;
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(studentObj));
      toast.success("Profile locked! Loading predictor...");
      onVerified(studentObj);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to lock SML rank.");
    } finally { setLoading(false); }
  };

  const iS = {
    width: "100%", boxSizing: "border-box", padding: "0.75rem 1rem",
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, color: "#f8fafc", fontSize: "0.88rem", fontFamily: "inherit", outline: "none"
  };
  const lS = { display: "flex", alignItems: "center", gap: 6, fontSize: "0.73rem", fontWeight: 700, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" };
  const isOpen = category === "OPEN";

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #0a0f1e 0%, #0d1b2a 50%, #0f1f16 100%)",
      padding: "1.5rem", fontFamily: "'Inter', -apple-system, sans-serif"
    }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
          <div style={{
            fontSize: "0.68rem", fontWeight: 800, color: "#f59e0b",
            letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 12, display: "block",
          }}>🔒 Enrolled Students Only</div>
          <h1 style={{ fontSize: "1.6rem", fontWeight: 800, color: "#f8fafc", margin: "0 0 8px" }}>
            {step === "LOGIN" ? "NEET UG Predictor" : "One-Time SML Registration"}
          </h1>
          <p style={{ fontSize: "0.82rem", color: "#64748b", margin: 0, lineHeight: 1.6 }}>
            {step === "LOGIN" ? (
              <>
                Verify your enrollment to access the<br />
                <strong style={{ color: "#94a3b8" }}>Maharashtra State Quota (85%) Predictor</strong>
              </>
            ) : (
              <>
                Candidate: <strong style={{ color: "#f59e0b" }}>{candidateInfo?.candidate_name}</strong><br />
                <span style={{ color: "#94a3b8" }}>Please enter your verified SML details to permanently lock your profile.</span>
              </>
            )}
          </p>
        </div>

        <div style={{
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 20, padding: "2rem", backdropFilter: "blur(12px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
        }}>
          {step === "LOGIN" ? (
            <form onSubmit={handleInitialVerify}>
              <div style={{ marginBottom: 14 }}>
                <label style={lS}><Hash size={12} /> Receipt / Registration Number *</label>
                <input style={iS} placeholder="e.g. MED 0232" value={regNo}
                  onChange={e => setRegNo(e.target.value.toUpperCase())} disabled={loading} autoFocus />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={lS}><TrendingUp size={12} /> All India Rank (AIR) *</label>
                <input style={iS} type="number" placeholder="e.g. 183432" value={air}
                  onChange={e => setAir(e.target.value)} disabled={loading} min={1} />
              </div>

              {error && (
                <div style={{
                  background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                  display: "flex", alignItems: "flex-start", gap: 8,
                }}>
                  <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: "0.8rem", color: "#fca5a5", lineHeight: 1.5 }}>{error}</span>
                </div>
              )}

              <button type="submit" disabled={loading} style={{
                width: "100%", padding: "0.85rem", borderRadius: 12, border: "none",
                background: loading ? "#334155" : "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "#fff", fontSize: "0.9rem", fontWeight: 700, fontFamily: "inherit",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: loading ? "none" : "0 0 24px rgba(245,158,11,0.25)",
                transition: "all 0.2s",
              }}>
                {loading ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Shield size={16} />}
                {loading ? "Verifying..." : "Verify & Access Predictor"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLockSmlSubmit}>
              <div style={{
                background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
                borderRadius: 10, padding: "10px 12px", marginBottom: 16,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: "0.65rem", color: "#f59e0b", fontWeight: 800, textTransform: "uppercase" }}>Enrolled Student</div>
                  <div style={{ fontSize: "0.85rem", color: "#f8fafc", fontWeight: 700 }}>{candidateInfo?.candidate_name}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.65rem", color: "#94a3b8" }}>{candidateInfo?.reg_no}</div>
                  <div style={{ fontSize: "0.78rem", color: "#38bdf8", fontWeight: 700 }}>AIR: {candidateInfo?.air?.toLocaleString("en-IN")}</div>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={lS}><Award size={12} /> State Merit List Rank (SML) *</label>
                <input style={iS} type="number" placeholder="e.g. 4600" value={sml}
                  onChange={e => setSml(e.target.value)} disabled={loading} min={1} autoFocus />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={lS}><User size={12} /> Category *</label>
                  <select style={{ ...iS, appearance: "none", cursor: "pointer" }} value={category}
                    onChange={e => { setCategory(e.target.value); if (e.target.value === "OPEN") setCategoryRank(""); }}
                    disabled={loading}>
                    {MAHA_CATEGORIES.map(c => <option key={c} value={c} style={{ background: "#0f172a" }}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lS}><User size={12} /> Gender *</label>
                  <select style={{ ...iS, appearance: "none", cursor: "pointer" }} value={gender}
                    onChange={e => setGender(e.target.value)} disabled={loading}>
                    <option value="Male" style={{ background: "#0f172a" }}>Male</option>
                    <option value="Female" style={{ background: "#0f172a" }}>Female</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ ...lS, color: isOpen ? "#334155" : "#94a3b8" }}>
                  <Award size={12} /> Category Rank {isOpen ? "(Not required for OPEN)" : "*"}
                </label>
                <input
                  style={{ ...iS, opacity: isOpen ? 0.4 : 1, cursor: isOpen ? "not-allowed" : "text" }}
                  type="number" placeholder={isOpen ? "Not applicable for OPEN category" : "e.g. 102"}
                  value={categoryRank} onChange={e => setCategoryRank(e.target.value)}
                  disabled={loading || isOpen} min={1} />
              </div>

              {error && (
                <div style={{
                  background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                  display: "flex", alignItems: "flex-start", gap: 8,
                }}>
                  <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: "0.8rem", color: "#fca5a5", lineHeight: 1.5 }}>{error}</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => { setStep("LOGIN"); setError(""); }} disabled={loading} style={{
                  padding: "0.85rem 1.2rem", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "#94a3b8", fontSize: "0.85rem", fontWeight: 700,
                  cursor: "pointer",
                }}>
                  Back
                </button>
                <button type="submit" disabled={loading} style={{
                  flex: 1, padding: "0.85rem", borderRadius: 12, border: "none",
                  background: loading ? "#334155" : "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff", fontSize: "0.9rem", fontWeight: 700, fontFamily: "inherit",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: loading ? "none" : "0 0 24px rgba(16,185,129,0.25)",
                }}>
                  {loading ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Shield size={16} />}
                  {loading ? "Locking Profile..." : "Confirm & Lock SML"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   PDF GENERATOR — "Download My Priority List"
═══════════════════════════════════════════════════════ */
export function generatePdfHtml(student, predictions, course, priorityList = null) {
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const items = priorityList && priorityList.length > 0 ? priorityList : predictions;
  const isPriorityMode = !!(priorityList && priorityList.length > 0);

  const govtCount    = items.filter(p => (p.status || "").toLowerCase().includes("govt") || (p.status || "").toLowerCase().includes("corp")).length;
  const privateCount = items.filter(p => !(p.status || "").toLowerCase().includes("govt") && !(p.status || "").toLowerCase().includes("corp")).length;
  const safeCnt     = items.filter(p => !p.is_new_college && p.cutoff_rank != null && getTier(p.margin_percent) === "Safe").length;
  const possibleCnt = items.filter(p => !p.is_new_college && p.cutoff_rank != null && getTier(p.margin_percent) === "Possible").length;
  const targetCnt   = items.filter(p => !p.is_new_college && p.cutoff_rank != null && getTier(p.margin_percent) === "Target").length;
  const dreamCnt    = items.filter(p => !p.is_new_college && p.cutoff_rank != null && getTier(p.margin_percent) === "Dream").length;

  const TIER_COLORS_PDF = {
    Safe:     { bg: "#d1fae5", border: "#6ee7b7", text: "#065f46" },
    Possible: { bg: "#dbeafe", border: "#93c5fd", text: "#1e3a8a" },
    Target:   { bg: "#fef3c7", border: "#fcd34d", text: "#78350f" },
    Dream:    { bg: "#ffe4e6", border: "#fca5a5", text: "#881337" },
  };

  const rows = items.map((p, i) => {
    const isNew   = !!p.is_new_college || p.cutoff_rank == null;
    const tier    = isNew ? null : getTier(p.margin_percent);
    const tc      = tier ? (TIER_COLORS_PDF[tier] || TIER_COLORS_PDF.Dream) : null;
    const seqNum  = p.pref_order != null ? p.pref_order : (p.priority_order != null ? p.priority_order : (i + 1));
    const choiceCode = (p.college_code && p.college_code !== "NEW") ? p.college_code : "—";

    const isGovt = (p.status || "").toLowerCase().includes("govt");
    const isCorp = (p.status || "").toLowerCase().includes("corp");
    const typeLabel = isGovt ? "GOVT" : isCorp ? "CORP" : "PRIVATE";
    const typeColor = isGovt ? "#065f46" : isCorp ? "#1e3a8a" : "#b45309";
    const typeBg    = isGovt ? "#d1fae5" : isCorp ? "#dbeafe" : "#fef3c7";

    const feesText = p.fees && p.fees !== "—"
      ? (String(p.fees).startsWith("₹") ? p.fees : "₹ " + p.fees)
      : "—";
    const hasNoFee = (p.college_name || "").includes("No Fee Benefits");

    const marginVal = p.margin_percent != null ? Math.round(p.margin_percent) : null;
    const tierCellHtml = isNew
      ? `<div style="font-size:8px;color:#d97706;font-weight:800;text-align:center;">NEW<br/>(2026-27)</div>`
      : `<div style="display:inline-block;padding:2px 7px;border-radius:4px;background:${tc.bg};border:1px solid ${tc.border};color:${tc.text};font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;">${tier}</div>
         ${marginVal != null ? `<div style="font-size:7.5px;color:#64748b;margin-top:2px;font-weight:600;">${marginVal > 0 ? "+" : ""}${marginVal}%</div>` : ""}`;

    const catMatch = p.all_matches?.["CAT-RANK"];
    const hasCatRank = student.category && student.category !== "OPEN" && student.category_rank;

    const r1Sml = p.r1_sml != null ? p.r1_sml.toLocaleString("en-IN") : null;
    const r2Sml = p.r2_sml != null ? p.r2_sml.toLocaleString("en-IN") : null;
    const r3Sml = p.r3_sml != null ? p.r3_sml.toLocaleString("en-IN") : null;

    const r1Cat = p.r1_cat_rank != null ? p.r1_cat_rank.toLocaleString("en-IN") : (catMatch?.cutoff_rank != null && p.r1_sml ? catMatch.cutoff_rank.toLocaleString("en-IN") : null);
    const r2Cat = p.r2_cat_rank != null ? p.r2_cat_rank.toLocaleString("en-IN") : null;
    const r3Cat = p.r3_cat_rank != null ? p.r3_cat_rank.toLocaleString("en-IN") : null;

    const formatRoundCell = (smlVal, catVal, roundName) => {
      if (!smlVal && !catVal) return `<div style="font-size:9px;color:#94a3b8;font-weight:600;">—</div>`;
      return `
        ${smlVal ? `<div style="font-size:8px;font-weight:800;color:#0f172a;line-height:1.2;">SML: ${smlVal}</div>` : ""}
        ${hasCatRank && catVal ? `<div style="font-size:7.5px;font-weight:800;color:#d97706;margin-top:1px;line-height:1.2;">${student.category}: ${catVal}</div>` : ""}
        <div style="font-size:6.5px;color:#94a3b8;font-weight:700;margin-top:1px;">${roundName}</div>
      `;
    };
    const noFeeNote = hasNoFee ? `<div style="font-size:7px;color:#dc2626;font-weight:800;margin-top:2px;">⚠️ No Fee Benefits</div>` : "";

    return `<tr style="page-break-inside:avoid;">
      <td style="padding:6px 3px;border-bottom:1px solid #e2e8f0;text-align:center;font-size:11px;font-weight:900;color:#0f172a;vertical-align:middle;">${seqNum}</td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;font-size:8.5px;color:#0369a1;font-weight:800;font-family:monospace;vertical-align:middle;">${choiceCode}</td>
      <td style="padding:6px 6px;border-bottom:1px solid #e2e8f0;vertical-align:middle;">
        <div style="font-size:9.5px;font-weight:700;color:#0f172a;line-height:1.3;">${p.college_name}</div>
        ${isNew ? `<div style="font-size:7.5px;color:#d97706;font-weight:800;margin-top:1px;">🆕 NEW COLLEGE (2026-27)</div>` : ""}
        ${noFeeNote}
      </td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;vertical-align:middle;">
        <div style="display:inline-block;padding:2px 6px;border-radius:4px;background:${typeBg};color:${typeColor};font-size:7.5px;font-weight:800;letter-spacing:0.3px;">${typeLabel}</div>
      </td>
      <td style="padding:6px 5px;border-bottom:1px solid #e2e8f0;vertical-align:middle;text-align:center;">
        ${feesText !== "—"
          ? `<div style="font-size:9px;color:#7c3aed;font-weight:700;">${feesText}</div><div style="font-size:6.5px;color:#94a3b8;margin-top:1px;">*Prev. Year · Check 2026-27</div>${hasNoFee ? `<div style="font-size:6.5px;color:#dc2626;font-weight:800;">⚠️ No Fee Benefits</div>` : ""}`
          : `<div style="font-size:9px;color:#94a3b8;font-weight:600;">—</div>`
        }
      </td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;vertical-align:middle;">${tierCellHtml}</td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;vertical-align:middle;">
        ${formatRoundCell(r1Sml, r1Cat, "R1")}
      </td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;vertical-align:middle;">
        ${formatRoundCell(r2Sml, r2Cat, "R2")}
      </td>
      <td style="padding:6px 4px;border-bottom:1px solid #e2e8f0;text-align:center;vertical-align:middle;">
        ${formatRoundCell(r3Sml, r3Cat, "R3")}
      </td>
    </tr>`;
  }).join("");

  return `<!DOCTYPE html><html><head>
    <title>My Priority List — ${student.candidate_name} (${course})</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; background:#fff; color:#1e293b; padding: 10px 12px; width: 100%; margin: 0; }
      .header  { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2.5px solid #f59e0b; padding-bottom:10px; margin-bottom:12px; }
      .logo-text { font-size:18px; font-weight:800; color:#0f172a; }
      .logo-text span { color:#f59e0b; }
      .subtitle { font-size:8.5px; text-transform:uppercase; letter-spacing:1px; color:#64748b; font-weight:700; margin-top:2px; }
      .badge { background:${isPriorityMode ? "#dbeafe" : "#fef3c7"}; color:${isPriorityMode ? "#1e3a8a" : "#92400e"}; padding:3px 10px; border-radius:50px; font-size:8px; font-weight:800; text-transform:uppercase; letter-spacing:0.8px; border:1px solid ${isPriorityMode ? "#93c5fd" : "#fcd34d"}; display:inline-block; }
      .student-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px; margin-bottom:12px; page-break-inside:avoid; }
      .student-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px 10px; }
      .info-label { font-size:7.5px; text-transform:uppercase; letter-spacing:0.6px; color:#94a3b8; font-weight:800; }
      .info-value { font-size:11px; font-weight:800; color:#0f172a; margin-top:2px; }
      .info-value.highlight { color:#f59e0b; }
      .section-title { font-size:8.5px; text-transform:uppercase; letter-spacing:0.8px; color:#475569; font-weight:800; margin-bottom:8px; }
      .balance { display:flex; gap:6px; margin-bottom:12px; page-break-inside:avoid; }
      .bal-item { flex:1; background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:7px 8px; text-align:center; }
      .bal-num  { font-size:16px; font-weight:800; line-height:1; }
      .bal-lbl  { font-size:7px; color:#64748b; font-weight:700; text-transform:uppercase; letter-spacing:0.4px; margin-top:2px; }
      table { width:100%; border-collapse:collapse; page-break-inside:auto; table-layout:fixed; }
      thead tr { background:#0f172a; }
      th { padding:6px 4px; color:#94a3b8; font-size:7.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; vertical-align:middle; text-align:center; }
      th:nth-child(3) { text-align:left; }
      tr:nth-child(even) td { background:#f8fafc; }
      .footer { margin-top:16px; border-top:1px solid #e2e8f0; padding-top:8px; display:flex; justify-content:space-between; font-size:8px; color:#94a3b8; page-break-inside:avoid; }
      .disclaimer { margin-top:12px; background:#fef9ec; border:1px solid #fde68a; border-radius:6px; padding:8px 10px; page-break-inside:avoid; }
    </style>
  </head><body>

    <div class="header">
      <div>
        <div class="logo-text">Admissions <span>Made Easy</span></div>
        <div class="subtitle">NEET UG · Maharashtra State Quota (85%) — ${isPriorityMode ? "My Priority List" : "Prediction Report"}</div>
      </div>
      <div style="text-align:right;">
        <div class="badge">${isPriorityMode ? "📋 Priority List" : "Student Report"}</div>
        <div style="font-size:8.5px;color:#64748b;margin-top:4px;">${today}</div>
        <div style="font-size:8px;color:#94a3b8;margin-top:1px;">admissionsmadeeasy.in</div>
      </div>
    </div>

    <div class="student-card">
      <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:0.8px;color:#64748b;margin-bottom:8px;">Student Profile</div>
      <div class="student-grid">
        <div><div class="info-label">Student Name</div><div class="info-value">${student.candidate_name}</div></div>
        <div><div class="info-label">Reg. Number</div><div class="info-value">${student.reg_no}</div></div>
        <div><div class="info-label">All India Rank</div><div class="info-value">${student.air?.toLocaleString("en-IN") || "—"}</div></div>
        <div><div class="info-label">SML Rank</div><div class="info-value highlight">${student.sml?.toLocaleString("en-IN") || "—"}</div></div>
        ${student.category_rank ? `<div><div class="info-label">Category Rank</div><div class="info-value highlight">${student.category_rank?.toLocaleString("en-IN")}</div></div>` : ""}
        <div><div class="info-label">Category</div><div class="info-value">${student.category || "—"}</div></div>
        <div><div class="info-label">Gender</div><div class="info-value">${student.gender || "—"}</div></div>
        <div><div class="info-label">Course</div><div class="info-value">${course}</div></div>
      </div>
    </div>

    <div class="section-title">${isPriorityMode ? "Priority List Summary" : "Prediction Summary"}</div>
    <div class="balance">
      <div class="bal-item"><div class="bal-num" style="color:#065f46;">${safeCnt}</div><div class="bal-lbl">Safe</div></div>
      <div class="bal-item"><div class="bal-num" style="color:#1e3a8a;">${possibleCnt}</div><div class="bal-lbl">Possible</div></div>
      <div class="bal-item"><div class="bal-num" style="color:#78350f;">${targetCnt}</div><div class="bal-lbl">Target</div></div>
      <div class="bal-item"><div class="bal-num" style="color:#881337;">${dreamCnt}</div><div class="bal-lbl">Dream</div></div>
      ${isPriorityMode ? `<div class="bal-item"><div class="bal-num" style="color:#065f46;">${govtCount}</div><div class="bal-lbl">Govt/Corp</div></div>
      <div class="bal-item"><div class="bal-num" style="color:#b45309;">${privateCount}</div><div class="bal-lbl">Private</div></div>` : ""}
      <div class="bal-item"><div class="bal-num" style="color:#0f172a;">${items.length}</div><div class="bal-lbl">Total Choices</div></div>
    </div>

    <div class="section-title" style="margin-top:12px;">${isPriorityMode ? "My Priority List" : "College Predictions"} — ${course} · State Quota (85%)</div>
    <table>
      <thead><tr>
        <th style="width:4%;text-align:center;">Pref #</th>
        <th style="width:8%;text-align:center;">Choice Code</th>
        <th style="width:30%;text-align:left;">College Name</th>
        <th style="width:8%;text-align:center;">Type</th>
        <th style="width:13%;text-align:center;">Annual Fee</th>
        <th style="width:10%;text-align:center;">Cutoff Tier</th>
        <th style="width:7%;text-align:center;">R1 SML</th>
        <th style="width:7%;text-align:center;">R2 SML</th>
        <th style="width:7%;text-align:center;">R3 SML</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="disclaimer">
      <div style="font-size:9px;font-weight:800;color:#92400e;margin-bottom:4px;">⚠️ Important Disclaimer & Fee Notice</div>
      <div style="font-size:8px;color:#78350f;line-height:1.5;">
        • <strong>Fee Disclaimer:</strong> The annual fees shown are based on previous year approved fees. Please verify the current year (2026-27) fee structure from FRA / college official site.<br/>
        • <strong>Cutoff Data:</strong> R1/R2/R3 SML cutoffs shown are from 2024-25. Historical data is for reference only.<br/>
        • <strong>Prediction Disclaimer:</strong> Predictions based on historical cutoff trends and your SML / Category Rank. Actual allotments depend on seat matrix and counselling rounds. Always consult your AME counsellor.
      </div>
    </div>

    <div class="footer">
      <span>admissionsmadeeasy.in</span>
      <span>${isPriorityMode ? "My Priority List" : "Prediction Report"} — ${student.candidate_name} (${student.reg_no})</span>
      <span>${new Date().toLocaleString("en-IN")}</span>
    </div>
  </body></html>`;
}

/* ═══════════════════════════════════════════════════════
   VERIFIED PREDICTOR TOOL & PRIORITY BUILDER
═══════════════════════════════════════════════════════ */
export function VerifiedPredictor({ student, onLogout, onUpdateStudent, isAdminMode = false, adminSavedStudents = [], onSwitchStudent, onBulkUpload, onDownloadTemplate }) {
  const [course,       setCourse]       = useState("MBBS");
  const [predictions,  setPredictions]  = useState([]);
  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [hasSearched,  setHasSearched]  = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter,   setTypeFilter]   = useState("ALL");
  const [tierFilter,   setTierFilter]   = useState("ALL");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adminSearch, setAdminSearch] = useState("");
  const resultsRef = useRef(null);

  /* ── Priority List State ── */
  const [activeTab,       setActiveTab]       = useState("predictor"); // "predictor" | "priority"
  const [priorityList,    setPriorityList]    = useState([]);
  const [isSavingPref,    setIsSavingPref]    = useState(false);
  const [isLoadingPref,   setIsLoadingPref]   = useState(false);
  const [prefSavedAt,     setPrefSavedAt]     = useState(null);
  const [prefSearchQ,     setPrefSearchQ]     = useState("");
  const [isAddModalOpen,  setIsAddModalOpen]  = useState(false);
  const [insertAtIndex,   setInsertAtIndex]   = useState(null); // position to insert at
  const [addSearchQ,      setAddSearchQ]      = useState("");
  const [addTypeFilter,   setAddTypeFilter]   = useState("ALL");

  /* ── Load saved priority list from cloud on mount / course change ── */
  useEffect(() => {
    if (!student?.reg_no || !course) return;
    const loadPrefs = async () => {
      setIsLoadingPref(true);
      try {
        const res = await api.get(`/medical-predictor/preferences/${encodeURIComponent(student.reg_no)}?course=${course}`);
        if (res.data?.found && res.data?.data?.preferences?.length > 0) {
          setPriorityList(res.data.data.preferences);
          setPrefSavedAt(res.data.data.saved_at);
        } else {
          setPriorityList([]);
          setPrefSavedAt(null);
        }
      } catch {
        setPriorityList([]);
      } finally {
        setIsLoadingPref(false);
      }
    };
    loadPrefs();
  }, [student?.reg_no, course]);

  /* ── Save priority list to cloud ── */
  const handleSavePrefs = async (listToSave = priorityList) => {
    if (!student?.reg_no) return;
    setIsSavingPref(true);
    try {
      const prefs = listToSave.map((p, i) => ({ ...p, pref_order: i + 1 }));
      await api.post("/medical-predictor/preferences/save", {
        reg_no: student.reg_no,
        course,
        preferences: prefs,
        student_name: student.candidate_name,
      });
      setPrefSavedAt(new Date().toISOString());
      toast.success(`Priority list saved (${prefs.length} choices)`);
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSavingPref(false);
    }
  };

  /* ── Open Insert Modal at specified index ── */
  const openInsertModal = (idx = priorityList.length) => {
    setInsertAtIndex(idx);
    setAddSearchQ("");
    setAddTypeFilter("ALL");
    setIsAddModalOpen(true);
  };

  /* ── Insert college at specific position ── */
  const insertCollegeAt = (college, idx) => {
    if (priorityList.some(p => p.college_name === college.college_name && p.matched_category === college.matched_category)) {
      toast.info("Already in your priority list");
      return;
    }
    const targetIdx = idx != null ? idx : priorityList.length;
    const updated = [
      ...priorityList.slice(0, targetIdx),
      { ...college },
      ...priorityList.slice(targetIdx)
    ].map((p, i) => ({ ...p, pref_order: i + 1 }));

    setPriorityList(updated);
    setIsAddModalOpen(false);
    toast.success(`Inserted at #${targetIdx + 1}: ${college.college_name.substring(0, 30)}...`);
  };

  /* ── Add college to priority list ── */
  const addToPriorityList = (college) => {
    if (priorityList.some(p => p.college_name === college.college_name && p.matched_category === college.matched_category)) {
      toast.info("Already in your priority list");
      return;
    }
    const updated = [...priorityList, { ...college, pref_order: priorityList.length + 1 }];
    setPriorityList(updated);
    toast.success(`Added #${updated.length}: ${college.college_name.substring(0, 30)}...`);
  };

  /* ── Remove from priority list ── */
  const removeFromPriorityList = (idx) => {
    const updated = priorityList.filter((_, i) => i !== idx).map((p, i) => ({ ...p, pref_order: i + 1 }));
    setPriorityList(updated);
    toast.info("Removed from priority list");
  };

  /* ── Move up ── */
  const movePriorityUp = (idx) => {
    if (idx === 0) return;
    const updated = [...priorityList];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setPriorityList(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
  };

  /* ── Move down ── */
  const movePriorityDown = (idx) => {
    if (idx === priorityList.length - 1) return;
    const updated = [...priorityList];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setPriorityList(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
  };

  /* ── Move to top ── */
  const movePriorityToTop = (idx) => {
    if (idx === 0) return;
    const item = priorityList[idx];
    const updated = [item, ...priorityList.filter((_, i) => i !== idx)];
    setPriorityList(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
  };

  /* ── Move to bottom ── */
  const movePriorityToBottom = (idx) => {
    if (idx === priorityList.length - 1) return;
    const item = priorityList[idx];
    const updated = [...priorityList.filter((_, i) => i !== idx), item];
    setPriorityList(updated.map((p, i) => ({ ...p, pref_order: i + 1 })));
  };

  /* ── Auto-fill from predictions ── */
  const autoFillFromPredictions = () => {
    if (!predictions.length) { toast.error("Please run the predictor first"); return; }
    const filled = predictions.map((p, i) => ({ ...p, pref_order: i + 1 }));
    setPriorityList(filled);
    toast.success(`Auto-filled ${filled.length} colleges based on prediction hierarchy`);
  };

  /* ── Clear priority list ── */
  const clearPriorityList = () => {
    if (window.confirm("Are you sure you want to clear your current priority list?")) {
      setPriorityList([]);
      toast.info("Priority list cleared");
    }
  };

  /* ── Download priority list PDF ── */
  const handlePriorityPdf = () => {
    if (!priorityList.length) { toast.error("Your priority list is empty"); return; }
    toast.info("Generating Priority List PDF…");
    const html = generatePdfHtml(student, predictions, course, priorityList);
    const el = document.createElement("div");
    el.innerHTML = html;
    import("html2pdf.js").then(({ default: html2pdf }) => {
      html2pdf().set({
        margin: [6, 6, 8, 6],
        filename: `MyPriorityList_${student.reg_no}_${course}.pdf`,
        image: { type: "jpeg", quality: 0.88 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape", compress: true },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] }
      }).from(el).save().then(() => toast.success("Priority List PDF downloaded!"));
    }).catch(() => toast.error("PDF generation failed."));
  };

  const handleOpenEdit = () => {
    setEditForm({
      candidate_name: student.candidate_name || "",
      air: student.air || "",
      sml: student.sml || "",
      category: student.category || "OPEN",
      category_rank: student.category_rank || "",
      gender: student.gender || "Male"
    });
    setIsEditModalOpen(true);
  };

  const runPredict = useCallback(async (c) => {
    if (!student.sml || student.sml === "") {
      setError("Please enter SML and AIR to generate predictions.");
      setPredictions([]);
      setSummary(null);
      setHasSearched(false);
      setLoading(false);
      return;
    }
    setError(""); setLoading(true); setPredictions([]); setSummary(null); setHasSearched(true);
    try {
      const body = {
        stream: "STATE_QUOTA",
        course: c,
        category: student.category,
        gender: student.gender,
        sml: student.sml,
        category_rank: student.category_rank || null,
        air: student.air,
        prediction_mode: (student.category !== "OPEN" && student.category_rank) ? "ALL" : "SML",
        include_air: false,
        target_round: "ALL",
      };
      const res = await api.post("/medical-predictor/predict", body);
      setPredictions(res.data.predictions || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Prediction failed.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setLoading(false); }
  }, [student]);

  useEffect(() => { runPredict(course); }, [course, runPredict]);

  /* ── Derived stats ── */
  const tierCounts = { Safe: 0, Possible: 0, Target: 0, Dream: 0 };
  predictions.forEach(p => {
    if (!p.is_new_college && p.cutoff_rank != null) {
      const t = getTier(p.margin_percent);
      if (tierCounts[t] !== undefined) tierCounts[t]++;
    }
  });

  /* ── Filtered predictions ── */
  const filtered = predictions.filter(p => {
    const isNew  = !!p.is_new_college || p.cutoff_rank == null;
    const stat   = (p.status || "").toLowerCase();
    const t      = isNew ? null : getTier(p.margin_percent);
    const matchS = !searchFilter || p.college_name.toLowerCase().includes(searchFilter.toLowerCase());
    const matchType = typeFilter === "ALL"
      || (typeFilter === "GOVT"    && (stat.includes("govt") || stat.includes("corp")))
      || (typeFilter === "PRIVATE" && !stat.includes("govt") && !stat.includes("corp"));
    const matchTier = tierFilter === "ALL" || (t && t === tierFilter);
    return matchS && matchType && matchTier;
  });

  /* ── Filtered Priority List ── */
  const filteredPriorityList = priorityList.filter(p => {
    if (!prefSearchQ) return true;
    return p.college_name.toLowerCase().includes(prefSearchQ.toLowerCase()) ||
           (p.college_code || "").toLowerCase().includes(prefSearchQ.toLowerCase());
  });

  /* ── Standard Predictor PDF ── */
  const handlePdf = () => {
    if (!predictions.length) return;
    toast.info("Generating Predictor PDF…");
    const html = generatePdfHtml(student, predictions, course);
    const el = document.createElement("div");
    el.innerHTML = html;
    import("html2pdf.js").then(({ default: html2pdf }) => {
      html2pdf().set({
        margin: [8, 8, 10, 8],
        filename: `NEET_StateQuota_${student.reg_no}_${course}.pdf`,
        image: { type: "jpeg", quality: 0.86 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", letterRendering: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      }).from(el).save().then(() => toast.success("Predictor PDF downloaded!"));
    }).catch(() => toast.error("PDF generation failed."));
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0a0f1e 0%, #0d1b2a 50%, #0f1f16 100%)",
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>

      {/* ── Sticky Header with Student Profile ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(10,15,30,0.95)", backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "0.65rem 1rem",
      }}>
        <div style={{
          maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 10, flexWrap: "wrap"
        }}>
          {/* Student Pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flex: "1 1 auto" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
              borderRadius: 40, padding: "4px 12px 4px 6px",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: "0.7rem", color: "#fff",
              }}>{student.candidate_name?.[0] || "S"}</div>
              <div>
                <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#fbbf24", lineHeight: 1 }}>{student.candidate_name}</div>
                <div style={{ fontSize: "0.58rem", color: "#64748b", marginTop: 1 }}>{student.reg_no}</div>
              </div>
            </div>

            {/* Rank Tags */}
            {[
              { label: "SML",      value: student.sml?.toLocaleString("en-IN"),          hi: true },
              student.category_rank && { label: "Cat Rank", value: student.category_rank?.toLocaleString("en-IN"), hi: true },
              { label: "AIR",      value: student.air?.toLocaleString("en-IN"),          hi: false },
              { label: student.category, value: student.gender, hi: false },
            ].filter(Boolean).map((t, i) => (
              <span key={i} style={{
                fontSize: "0.62rem", fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                background: t.hi ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.04)",
                color: t.hi ? "#f59e0b" : "#94a3b8",
                border: `1px solid ${t.hi ? "rgba(245,158,11,0.25)" : "rgba(255,255,255,0.08)"}`,
                whiteSpace: "nowrap",
              }}>{t.label}: {t.value}</span>
            ))}
          </div>

          {/* Header Action Buttons */}
          <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
            {isAdminMode && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1px solid rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.1)", color: "#10b981", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  <FileUp size={12} /> Upload Excel
                  <input type="file" accept=".xlsx, .xls" style={{ display: "none" }} onChange={onBulkUpload} />
                </label>
                <button onClick={onDownloadTemplate} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1px solid rgba(16,185,129,0.3)", background: "transparent", color: "#10b981", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  <FileDown size={12} /> Template
                </button>
                <button onClick={() => setIsSidebarOpen(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1px solid rgba(139,92,246,0.3)", background: "rgba(139,92,246,0.1)", color: "#c4b5fd", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  <Users size={12} /> Switch Student
                </button>
                <button onClick={handleOpenEdit} style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7,
                  border: "1px solid rgba(245,158,11,0.2)", background: "rgba(245,158,11,0.05)",
                  color: "#f59e0b", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}><User size={12} /> Edit SML</button>
              </>
            )}

            {/* Always-Visible Download My Priority List Button if priority items exist */}
            {priorityList.length > 0 && (
              <button
                onClick={handlePriorityPdf}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 7,
                  border: "1px solid rgba(139,92,246,0.5)", background: "linear-gradient(135deg, rgba(139,92,246,0.3), rgba(109,40,217,0.3))",
                  color: "#e9d5ff", fontSize: "0.74rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  boxShadow: "0 0 16px rgba(139,92,246,0.25)"
                }}
              >
                <Download size={13} /> Download My Priority List
              </button>
            )}

            {hasSearched && predictions.length > 0 && activeTab === "predictor" && (
              <button onClick={handlePdf} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7,
                border: "1px solid rgba(56,189,248,0.25)", background: "rgba(56,189,248,0.08)",
                color: "#38bdf8", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}><Download size={12} /> Predictor PDF</button>
            )}

            <button onClick={onLogout} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7,
              border: "1px solid rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.05)",
              color: "#f87171", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}><LogOut size={12} /> {isAdminMode ? "Close Profile" : "Logout"}</button>
          </div>
        </div>
      </div>

      {/* ── TOP NAV TABS: PREDICTOR vs PRIORITY BUILDER ── */}
      <div style={{ background: "rgba(0,0,0,0.35)", borderBottom: "1px solid rgba(255,255,255,0.09)" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 1rem", display: "flex", gap: 6 }}>
          <button
            onClick={() => setActiveTab("predictor")}
            style={{
              padding: "12px 20px", border: "none",
              borderBottom: activeTab === "predictor" ? "3px solid #f59e0b" : "3px solid transparent",
              background: activeTab === "predictor" ? "rgba(245,158,11,0.08)" : "transparent",
              color: activeTab === "predictor" ? "#fbbf24" : "#94a3b8",
              fontSize: "0.84rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
            }}
          >
            🎯 Predicted Colleges
            {predictions.length > 0 && (
              <span style={{
                fontSize: "0.64rem", fontWeight: 800, padding: "2px 7px", borderRadius: 10,
                background: "rgba(245,158,11,0.25)", color: "#f59e0b",
              }}>{predictions.length}</span>
            )}
          </button>

          <button
            onClick={() => setActiveTab("priority")}
            style={{
              padding: "12px 20px", border: "none",
              borderBottom: activeTab === "priority" ? "3px solid #8b5cf6" : "3px solid transparent",
              background: activeTab === "priority" ? "rgba(139,92,246,0.1)" : "transparent",
              color: activeTab === "priority" ? "#c4b5fd" : "#94a3b8",
              fontSize: "0.84rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
            }}
          >
            📋 My Priority List Builder
            {priorityList.length > 0 && (
              <span style={{
                fontSize: "0.64rem", fontWeight: 800, padding: "2px 8px", borderRadius: 10,
                background: "rgba(139,92,246,0.35)", color: "#f3e8ff",
              }}>{priorityList.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Main Content Area ── */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "1.25rem 1rem" }}>

        {/* Title + Course Switcher */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: "1rem" }}>
          <div>
            <h1 style={{ fontSize: "1.18rem", fontWeight: 800, color: "#f8fafc", margin: "0 0 3px", display: "flex", alignItems: "center", gap: 8 }}>
              {activeTab === "predictor" ? "🩺 Maharashtra State Quota Predictor" : "📋 My Priority List Builder"}
              <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 400 }}>(85%)</span>
            </h1>
            <p style={{ fontSize: "0.74rem", color: "#64748b", margin: 0 }}>
              {activeTab === "predictor"
                ? `Predictions based on your ${student.category_rank ? "SML & Category Rank" : "SML"} · Gender: ${student.gender}`
                : "Customize your preference order (up/down/top/bottom), delete/add choices, save to cloud & download official priority list PDF."}
            </p>
          </div>
          <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 3, gap: 2 }}>
            {["MBBS", "BDS", "BAMS", "BHMS"].map(c => (
              <button key={c} onClick={() => setCourse(c)} style={{
                padding: "6px 18px", borderRadius: 8, border: "none",
                background: course === c ? "linear-gradient(135deg, #f59e0b, #d97706)" : "transparent",
                color: course === c ? "#fff" : "#64748b",
                fontSize: "0.76rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
              }}>{c}</button>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════
            TAB 1: PREDICTED COLLEGES WORKSPACE
        ═══════════════════════════════════════════════════════ */}
        {activeTab === "predictor" && (
          <>
            {/* Tier Summary Filter Bar */}
            {!loading && hasSearched && predictions.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(95px, 1fr))",
                gap: 6, marginBottom: 12,
              }}>
                {[
                  { tier: "Safe",     count: tierCounts.Safe,     color: "#10b981", bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.25)"  },
                  { tier: "Possible", count: tierCounts.Possible, color: "#3b82f6", bg: "rgba(59,130,246,0.1)",  border: "rgba(59,130,246,0.25)"  },
                  { tier: "Target",   count: tierCounts.Target,   color: "#f59e0b", bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.25)"  },
                  { tier: "Dream",    count: tierCounts.Dream,    color: "#f43f5e", bg: "rgba(244,63,94,0.1)",   border: "rgba(244,63,94,0.25)"   },
                  { tier: "ALL",      count: predictions.length,  color: "#f8fafc", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)" },
                ].map(s => (
                  <button key={s.tier}
                    onClick={() => setTierFilter(s.tier === "ALL" ? "ALL" : (tierFilter === s.tier ? "ALL" : s.tier))}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "6px 8px", borderRadius: 8,
                      border: `1px solid ${tierFilter === s.tier || (s.tier === "ALL" && tierFilter === "ALL") ? s.border : "rgba(255,255,255,0.05)"}`,
                      background: tierFilter === s.tier || (s.tier === "ALL" && tierFilter === "ALL") ? s.bg : "rgba(255,255,255,0.02)",
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                    }}>
                    <span style={{ fontSize: "0.95rem", fontWeight: 800, color: s.color }}>{s.count}</span>
                    <span style={{ fontSize: "0.6rem", fontWeight: 700, color: s.tier === tierFilter ? s.color : "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {s.tier === "ALL" ? "Total" : s.tier}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Quick Priority Callout Bar */}
            {hasSearched && !loading && predictions.length > 0 && (
              <div style={{
                background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)",
                borderRadius: 10, padding: "10px 14px", marginBottom: 12,
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
              }}>
                <div style={{ fontSize: "0.76rem", color: "#c4b5fd" }}>
                  💡 <strong>Priority List Builder:</strong> Click <strong>"+ Add to Priority"</strong> on any college below to build your custom choice list, or open your builder to organize your choices.
                </div>
                <button
                  onClick={() => setActiveTab("priority")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "6px 16px", borderRadius: 8, border: "none",
                    background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    color: "#fff", fontSize: "0.76rem", fontWeight: 800, cursor: "pointer",
                    boxShadow: "0 0 16px rgba(139,92,246,0.3)"
                  }}
                >
                  <ListOrdered size={14} /> Create Your Own Priority List
                  {priorityList.length > 0 && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 10, background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 900, marginLeft: 2 }}>
                      {priorityList.length}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Filters Row */}
            {hasSearched && !loading && predictions.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
                <div style={{ position: "relative", flex: "1 1 200px", minWidth: 160 }}>
                  <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#475569" }} />
                  <input value={searchFilter} onChange={e => setSearchFilter(e.target.value)} placeholder="Search college name..."
                    style={{
                      width: "100%", boxSizing: "border-box", padding: "7px 10px 7px 30px",
                      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 8, color: "#f8fafc", fontSize: "0.76rem", fontFamily: "inherit", outline: "none",
                    }} />
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {["ALL", "GOVT", "PRIVATE"].map(f => (
                    <button key={f} onClick={() => setTypeFilter(f)} style={{
                      padding: "6px 12px", borderRadius: 8,
                      border: `1px solid ${typeFilter === f ? "rgba(245,158,11,0.4)" : "rgba(255,255,255,0.08)"}`,
                      background: typeFilter === f ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.03)",
                      color: typeFilter === f ? "#f59e0b" : "#64748b",
                      fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    }}>{f === "ALL" ? "All Types" : f}</button>
                  ))}
                </div>
                <span style={{ fontSize: "0.68rem", color: "#64748b", marginLeft: "auto" }}>
                  {filtered.length} of {predictions.length} colleges
                </span>
              </div>
            )}

            {/* Error Message */}
            {error && !loading && (
              <div style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: 10, padding: "12px 16px", marginBottom: 14,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <AlertCircle size={15} color="#ef4444" />
                <span style={{ fontSize: "0.82rem", color: "#fca5a5" }}>{error}</span>
              </div>
            )}

            {/* Loading Indicator */}
            {loading && (
              <div style={{ textAlign: "center", padding: "5rem 0" }}>
                <Loader2 size={36} color="#f59e0b" style={{ animation: "spin 1s linear infinite", marginBottom: 16 }} />
                <div style={{ fontSize: "0.88rem", color: "#64748b" }}>Predicting colleges for your SML{student.category_rank ? " & Category Rank" : ""}…</div>
              </div>
            )}

            {/* Predicted College Cards List */}
            {!loading && (
              <div ref={resultsRef}>
                {hasSearched && filtered.length === 0 && !error && (
                  <div style={{
                    textAlign: "center", padding: "4rem",
                    background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 14,
                  }}>
                    <GraduationCap size={38} color="#334155" style={{ marginBottom: 10 }} />
                    <div style={{ fontSize: "0.88rem", color: "#475569", fontWeight: 600 }}>No colleges matched filters</div>
                    <div style={{ fontSize: "0.72rem", color: "#334155", marginTop: 5 }}>Try clearing search or tier filter</div>
                  </div>
                )}
                {filtered.length > 0 && (
                  <div>
                    {filtered.map((p, i) => (
                      <CollegeCard
                        key={`${p.college_name}-${p.matched_category}-${i}`}
                        p={p}
                        index={i}
                        onAddPriority={addToPriorityList}
                        isInPriority={priorityList.some(item => item.college_name === p.college_name && item.matched_category === p.matched_category)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════
            TAB 2: MY PRIORITY LIST BUILDER WORKSPACE
        ═══════════════════════════════════════════════════════ */}
        {activeTab === "priority" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Action Bar */}
            <div style={{
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12, padding: "12px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "#f8fafc" }}>
                    Selected: {priorityList.length} Preferences
                  </span>
                  {prefSavedAt && (
                    <span style={{ fontSize: "0.65rem", color: "#10b981", background: "rgba(16,185,129,0.1)", padding: "2px 8px", borderRadius: 6, border: "1px solid rgba(16,185,129,0.2)" }}>
                      ✓ Saved to Cloud ({new Date(prefSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: 2 }}>
                  Use the mobile-friendly up/down buttons to customize your choice filling order
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => handleSavePrefs()}
                  disabled={isSavingPref || priorityList.length === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", borderRadius: 8, border: "none",
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    color: "#fff", fontSize: "0.76rem", fontWeight: 700, cursor: isSavingPref ? "not-allowed" : "pointer",
                    boxShadow: "0 0 16px rgba(16,185,129,0.25)"
                  }}
                >
                  <Save size={13} />
                  {isSavingPref ? "Saving..." : "Save List"}
                </button>

                <button
                  onClick={handlePriorityPdf}
                  disabled={priorityList.length === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(139,92,246,0.5)",
                    background: "linear-gradient(135deg, rgba(139,92,246,0.25), rgba(109,40,217,0.25))",
                    color: "#e9d5ff", fontSize: "0.76rem", fontWeight: 800, cursor: "pointer",
                  }}
                >
                  <Download size={13} /> Download My Priority List
                </button>

                <button
                  onClick={autoFillFromPredictions}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "7px 12px", borderRadius: 8, border: "1px solid rgba(245,158,11,0.3)",
                    background: "rgba(245,158,11,0.08)",
                    color: "#fbbf24", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
                  }}
                >
                  <RefreshCw size={12} /> Auto-Fill
                </button>

                {priorityList.length > 0 && (
                  <button
                    onClick={clearPriorityList}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.2)",
                      background: "rgba(239,68,68,0.05)",
                      color: "#f87171", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    <Trash2 size={12} /> Clear
                  </button>
                )}
              </div>
            </div>

            {/* Priority List Search Filter */}
            {priorityList.length > 5 && (
              <div style={{ position: "relative" }}>
                <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#475569" }} />
                <input
                  value={prefSearchQ}
                  onChange={e => setPrefSearchQ(e.target.value)}
                  placeholder="Filter your prioritized colleges..."
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "8px 10px 8px 32px",
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8, color: "#f8fafc", fontSize: "0.76rem", fontFamily: "inherit", outline: "none",
                  }}
                />
              </div>
            )}

            {/* Empty State */}
            {priorityList.length === 0 && (
              <div style={{
                textAlign: "center", padding: "4rem 1.5rem",
                background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(139,92,246,0.3)", borderRadius: 16,
              }}>
                <div style={{
                  width: 54, height: 54, borderRadius: "50%",
                  background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px", color: "#c4b5fd"
                }}>
                  <ListOrdered size={26} />
                </div>
                <h3 style={{ fontSize: "1.05rem", fontWeight: 800, color: "#f8fafc", margin: "0 0 6px" }}>
                  Your Priority List is Empty
                </h3>
                <p style={{ fontSize: "0.78rem", color: "#64748b", maxWidth: 440, margin: "0 auto 20px", lineHeight: 1.5 }}>
                  Build your official preference order. Click below to auto-populate all colleges based on cutoffs, or add them one-by-one from the Predicted Colleges tab.
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={autoFillFromPredictions}
                    style={{
                      padding: "9px 20px", borderRadius: 10, border: "none",
                      background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                      color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <ListOrdered size={14} /> Auto-Fill from Predictions
                  </button>
                  <button
                    onClick={() => setActiveTab("predictor")}
                    style={{
                      padding: "9px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.15)",
                      background: "transparent",
                      color: "#cbd5e1", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    Browse Predictions
                  </button>
                </div>
              </div>
            )}

            {/* Touch-Friendly Reorderable Items List */}
            {priorityList.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                
                {/* Insert at Top Button */}
                <div style={{ display: "flex", justifyContent: "center", margin: "2px 0 4px" }}>
                  <button
                    onClick={() => openInsertModal(0)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "4px 14px", borderRadius: 20,
                      background: "rgba(139,92,246,0.08)", border: "1px dashed rgba(139,92,246,0.35)",
                      color: "#c4b5fd", fontSize: "0.68rem", fontWeight: 700, cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,92,246,0.2)"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(139,92,246,0.08)"; e.currentTarget.style.color = "#c4b5fd"; }}
                  >
                    <Plus size={12} /> Insert College at #1 (Top)
                  </button>
                </div>

                {filteredPriorityList.map((item, idx) => {
                  const actualIndex = priorityList.findIndex(p => p.college_name === item.college_name && p.matched_category === item.matched_category);
                  const isNew = !!item.is_new_college || item.cutoff_rank == null;
                  const tier = isNew ? null : getTier(item.margin_percent);
                  const tc = tier ? (FOUR_TIER_COLORS[tier] || FOUR_TIER_COLORS.Dream) : null;
                  const isGovt = (item.status || "").toLowerCase().includes("govt");
                  const isCorp = (item.status || "").toLowerCase().includes("corp");

                  return (
                    <React.Fragment key={`${item.college_name}-${idx}`}>
                      <div
                        style={{
                          display: "flex",
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.07)",
                          borderLeft: `4px solid ${tier ? TIER_ACCENT[tier] : "#f59e0b"}`,
                          borderRadius: 10,
                          overflow: "hidden",
                          alignItems: "stretch",
                        }}
                      >
                      {/* Priority Rank # Badge */}
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 44,
                        background: "rgba(0,0,0,0.2)",
                        borderRight: "1px solid rgba(255,255,255,0.06)",
                        padding: "4px 2px",
                      }}>
                        <span style={{ fontSize: "0.55rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Pref</span>
                        <span style={{ fontSize: "0.92rem", fontWeight: 900, color: "#fbbf24" }}>#{actualIndex + 1}</span>
                      </div>

                      {/* Mobile-Friendly Move Up/Down Controls */}
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        padding: "4px 6px",
                        gap: 2,
                        borderRight: "1px solid rgba(255,255,255,0.06)",
                        background: "rgba(255,255,255,0.015)",
                      }}>
                        <button
                          onClick={() => movePriorityToTop(actualIndex)}
                          disabled={actualIndex === 0}
                          title="Jump to Top (#1)"
                          style={{
                            padding: "3px 5px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)",
                            background: actualIndex === 0 ? "transparent" : "rgba(255,255,255,0.05)",
                            color: actualIndex === 0 ? "#334155" : "#a78bfa",
                            cursor: actualIndex === 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                          }}
                        >
                          <ChevronsUp size={11} />
                        </button>
                        <button
                          onClick={() => movePriorityUp(actualIndex)}
                          disabled={actualIndex === 0}
                          title="Move Up 1 position"
                          style={{
                            padding: "4px 6px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)",
                            background: actualIndex === 0 ? "transparent" : "rgba(255,255,255,0.08)",
                            color: actualIndex === 0 ? "#334155" : "#38bdf8",
                            cursor: actualIndex === 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                          }}
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          onClick={() => movePriorityDown(actualIndex)}
                          disabled={actualIndex === priorityList.length - 1}
                          title="Move Down 1 position"
                          style={{
                            padding: "4px 6px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)",
                            background: actualIndex === priorityList.length - 1 ? "transparent" : "rgba(255,255,255,0.08)",
                            color: actualIndex === priorityList.length - 1 ? "#334155" : "#38bdf8",
                            cursor: actualIndex === priorityList.length - 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                          }}
                        >
                          <ArrowDown size={12} />
                        </button>
                        <button
                          onClick={() => movePriorityToBottom(actualIndex)}
                          disabled={actualIndex === priorityList.length - 1}
                          title="Jump to Bottom"
                          style={{
                            padding: "3px 5px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)",
                            background: actualIndex === priorityList.length - 1 ? "transparent" : "rgba(255,255,255,0.05)",
                            color: actualIndex === priorityList.length - 1 ? "#334155" : "#a78bfa",
                            cursor: actualIndex === priorityList.length - 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                          }}
                        >
                          <ChevronsDown size={11} />
                        </button>
                      </div>

                      {/* College Details Body */}
                      <div style={{ flex: 1, padding: "8px 12px", minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                          <div>
                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f8fafc", lineHeight: 1.3 }}>
                              {item.college_name}
                            </div>
                            <div style={{ fontSize: "0.6rem", color: "#64748b", marginTop: 2 }}>
                              Choice Code: <strong style={{ color: "#38bdf8" }}>{item.college_code || "—"}</strong> · {item.status?.toUpperCase() || "MAHARASHTRA"}
                            </div>
                          </div>

                          <button
                            onClick={() => removeFromPriorityList(actualIndex)}
                            style={{
                              padding: "4px 6px", borderRadius: 6, border: "none",
                              background: "rgba(239,68,68,0.1)", color: "#f87171",
                              cursor: "pointer", flexShrink: 0
                            }}
                            title="Remove from Priority List"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {/* Badges and Round Cutoffs */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6, alignItems: "center" }}>
                          {tier && (
                            <span style={{
                              fontSize: "0.58rem", padding: "1px 6px", borderRadius: 3, fontWeight: 800,
                              background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text,
                            }}>{tier}</span>
                          )}
                          {isNew && (
                            <span style={{
                              fontSize: "0.58rem", padding: "1px 6px", borderRadius: 3, fontWeight: 800,
                              background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)", color: "#f59e0b"
                            }}>NEW (2026-27)</span>
                          )}
                          <span style={{
                            fontSize: "0.55rem", padding: "1px 6px", borderRadius: 3, fontWeight: 700,
                            background: isGovt ? "rgba(16,185,129,0.1)" : isCorp ? "rgba(56,189,248,0.1)" : "rgba(245,158,11,0.1)",
                            color: isGovt ? "#10b981" : isCorp ? "#38bdf8" : "#f59e0b",
                          }}>
                            {isGovt ? "GOVT" : isCorp ? "CORP" : "PRIVATE"}
                          </span>

                          {item.fees && item.fees !== "—" && (
                            <span style={{ fontSize: "0.62rem", color: "#c084fc", fontWeight: 700 }}>
                              {String(item.fees).startsWith("₹") ? item.fees : `₹ ${item.fees}`}
                            </span>
                          )}

                          {/* Historical Round Cutoffs Pill */}
                          <div style={{ display: "inline-flex", gap: 4, marginLeft: "auto", fontSize: "0.58rem", color: "#94a3b8" }}>
                            <span style={{ background: "rgba(255,255,255,0.05)", padding: "1px 5px", borderRadius: 3 }}>
                              R1: <strong style={{ color: "#e2e8f0" }}>{item.r1_sml != null ? item.r1_sml.toLocaleString("en-IN") : "—"}</strong>
                            </span>
                            <span style={{ background: "rgba(255,255,255,0.05)", padding: "1px 5px", borderRadius: 3 }}>
                              R2: <strong style={{ color: "#e2e8f0" }}>{item.r2_sml != null ? item.r2_sml.toLocaleString("en-IN") : "—"}</strong>
                            </span>
                            <span style={{ background: "rgba(255,255,255,0.05)", padding: "1px 5px", borderRadius: 3 }}>
                              R3: <strong style={{ color: "#e2e8f0" }}>{item.r3_sml != null ? item.r3_sml.toLocaleString("en-IN") : "—"}</strong>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Inline Insert Button After Every College */}
                    <div style={{ display: "flex", justifyContent: "center", margin: "2px 0" }}>
                      <button
                        onClick={() => openInsertModal(actualIndex + 1)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "3px 12px", borderRadius: 20,
                          background: "rgba(139,92,246,0.06)", border: "1px dashed rgba(139,92,246,0.25)",
                          color: "#a78bfa", fontSize: "0.66rem", fontWeight: 700, cursor: "pointer",
                          transition: "all 0.15s"
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,92,246,0.18)"; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(139,92,246,0.06)"; e.currentTarget.style.color = "#a78bfa"; }}
                      >
                        <Plus size={11} /> Insert College at #{actualIndex + 2}
                      </button>
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Add at End Button */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                <button
                  onClick={() => openInsertModal(priorityList.length)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 18px", borderRadius: 10, border: "1px dashed rgba(16,185,129,0.4)",
                    background: "rgba(16,185,129,0.08)", color: "#34d399",
                    fontSize: "0.74rem", fontWeight: 800, cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(16,185,129,0.18)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(16,185,129,0.08)"}
                >
                  <Plus size={13} /> + Add College at End (#{priorityList.length + 1})
                </button>
              </div>
            </div>
          )}
          </div>
        )}

      </div>


      {/* ── Search & Insert College Modal ── */}
      {isAddModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16
        }}>
          <div style={{
            background: "#0a0f1e", border: "1px solid rgba(139,92,246,0.3)",
            borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "88vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 25px 60px rgba(0,0,0,0.7)"
          }}>
            {/* Modal Header */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, color: "#f8fafc", fontSize: "1.05rem", fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
                  <Plus size={16} color="#c4b5fd" />
                  Insert College at Position <span style={{ color: "#fbbf24" }}>#{insertAtIndex != null ? insertAtIndex + 1 : priorityList.length + 1}</span>
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "0.7rem", color: "#64748b" }}>
                  Search by College Name or Choice Code to insert directly into your preference order.
                </p>
              </div>
              <button onClick={() => setIsAddModalOpen(false)} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{ padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
                <input
                  autoFocus
                  value={addSearchQ}
                  onChange={e => setAddSearchQ(e.target.value)}
                  placeholder="Search by College Name (e.g. KEM, BJ) or Choice Code (e.g. 1103)..."
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px",
                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8, color: "#f8fafc", fontSize: "0.8rem", outline: "none"
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: 4 }}>
                {["ALL", "GOVT", "PRIVATE"].map(t => (
                  <button
                    key={t}
                    onClick={() => setAddTypeFilter(t)}
                    style={{
                      padding: "6px 12px", borderRadius: 8,
                      border: `1px solid ${addTypeFilter === t ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)"}`,
                      background: addTypeFilter === t ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.03)",
                      color: addTypeFilter === t ? "#c4b5fd" : "#94a3b8",
                      fontSize: "0.72rem", fontWeight: 700, cursor: "pointer"
                    }}
                  >
                    {t === "ALL" ? "All" : t}
                  </button>
                ))}
              </div>
            </div>

            {/* Colleges Result List */}
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 20px" }}>
              {(() => {
                const searchPool = predictions.length > 0 ? predictions : [];
                const filteredSearch = searchPool.filter(p => {
                  const s = addSearchQ.toLowerCase().trim();
                  const stat = (p.status || "").toLowerCase();
                  const matchS = !s ||
                    (p.college_name || "").toLowerCase().includes(s) ||
                    (p.college_code || "").toLowerCase().includes(s);
                  const matchType =
                    addTypeFilter === "ALL" ||
                    (addTypeFilter === "GOVT" && (stat.includes("govt") || stat.includes("corp"))) ||
                    (addTypeFilter === "PRIVATE" && !stat.includes("govt") && !stat.includes("corp"));
                  return matchS && matchType;
                });

                if (filteredSearch.length === 0) {
                  return (
                    <div style={{ padding: "3rem 1rem", textAlign: "center", color: "#64748b" }}>
                      <GraduationCap size={32} style={{ margin: "0 auto 8px", opacity: 0.5 }} />
                      <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>No colleges found matching "{addSearchQ}"</div>
                      <div style={{ fontSize: "0.7rem", marginTop: 4 }}>Try searching by partial name or 4-digit choice code.</div>
                    </div>
                  );
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {filteredSearch.map((p, idx) => {
                      const inListIdx = priorityList.findIndex(item => item.college_name === p.college_name && item.matched_category === p.matched_category);
                      const isAlreadyIn = inListIdx !== -1;
                      const isGovt = (p.status || "").toLowerCase().includes("govt");
                      const isCorp = (p.status || "").toLowerCase().includes("corp");
                      const typeLabel = isGovt ? "GOVT" : isCorp ? "CORP" : "PRIVATE";
                      const typeColor = isGovt ? "#10b981" : isCorp ? "#38bdf8" : "#f59e0b";

                      return (
                        <div
                          key={`${p.college_name}-${idx}`}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            padding: "9px 12px", borderRadius: 8,
                            background: isAlreadyIn ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.03)",
                            border: `1px solid ${isAlreadyIn ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.08)"}`,
                            opacity: isAlreadyIn ? 0.6 : 1
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f8fafc", lineHeight: 1.3 }}>
                              {p.college_name}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                              <span style={{ fontSize: "0.62rem", fontFamily: "monospace", color: "#38bdf8", fontWeight: 800 }}>
                                Code: {p.college_code || "—"}
                              </span>
                              <span style={{ fontSize: "0.58rem", padding: "1px 6px", borderRadius: 3, fontWeight: 800, background: "rgba(255,255,255,0.06)", color: typeColor }}>
                                {typeLabel}
                              </span>
                              {p.fees && p.fees !== "—" && (
                                <span style={{ fontSize: "0.62rem", color: "#c084fc", fontWeight: 700 }}>
                                  {String(p.fees).startsWith("₹") ? p.fees : `₹ ${p.fees}`}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ flexShrink: 0 }}>
                            {isAlreadyIn ? (
                              <span style={{ fontSize: "0.68rem", color: "#10b981", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                <Check size={12} /> Added (#{inListIdx + 1})
                              </span>
                            ) : (
                              <button
                                onClick={() => insertCollegeAt(p, insertAtIndex)}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "6px 12px", borderRadius: 6, border: "none",
                                  background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                                  color: "#fff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer",
                                  boxShadow: "0 0 12px rgba(139,92,246,0.3)"
                                }}
                              >
                                <Plus size={12} /> Insert at #{insertAtIndex != null ? insertAtIndex + 1 : priorityList.length + 1}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "10px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setIsAddModalOpen(false)}
                style={{
                  padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 600, fontSize: "0.76rem"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin Edit Modal ── */}
      {isEditModalOpen && editForm && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16
        }}>
          <div style={{
            background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 16, width: "100%", maxWidth: 450, padding: 24,
            boxShadow: "0 20px 40px rgba(0,0,0,0.5)"
          }}>
            <h3 style={{ margin: "0 0 16px", color: "#f8fafc", fontSize: "1.1rem", fontWeight: 700 }}>Edit Testing Details</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>Candidate Name</label>
                <input value={editForm.candidate_name} onChange={e => setEditForm({...editForm, candidate_name: e.target.value})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>AIR</label>
                  <input type="number" value={editForm.air} onChange={e => setEditForm({...editForm, air: e.target.value})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>SML</label>
                  <input type="number" value={editForm.sml} onChange={e => setEditForm({...editForm, sml: e.target.value})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>Category</label>
                  <select value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value, category_rank: e.target.value === "OPEN" ? "" : editForm.category_rank})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }}>
                    {MAHA_CATEGORIES.map(c => <option key={c} value={c} style={{ color: "#000" }}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>Category Rank</label>
                  <input type="number" disabled={editForm.category === "OPEN"} value={editForm.category_rank} onChange={e => setEditForm({...editForm, category_rank: e.target.value})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box", opacity: editForm.category === "OPEN" ? 0.5 : 1 }} />
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", marginBottom: 4 }}>Gender</label>
                <select value={editForm.gender} onChange={e => setEditForm({...editForm, gender: e.target.value})} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }}>
                  <option value="Male" style={{ color: "#000" }}>Male</option>
                  <option value="Female" style={{ color: "#000" }}>Female</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <button onClick={() => setIsEditModalOpen(false)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 600 }}>Cancel</button>
              <button onClick={() => {
                onUpdateStudent({
                  ...editForm,
                  air: editForm.air ? parseInt(editForm.air) : null,
                  sml: editForm.sml ? parseInt(editForm.sml) : null,
                  category_rank: editForm.category_rank ? parseInt(editForm.category_rank) : null
                });
                setIsEditModalOpen(false);
                toast.success("Details updated! Recalculating...");
              }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", cursor: "pointer", fontWeight: 600 }}>Save & Predict</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin Sidebar for Switching Students ── */}
      {isAdminMode && isSidebarOpen && (
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 340, maxWidth: "100%",
          background: "#0a0f1e", borderLeft: "1px solid rgba(255,255,255,0.08)",
          zIndex: 1000, display: "flex", flexDirection: "column", boxShadow: "-10px 0 30px rgba(0,0,0,0.5)"
        }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: "1.1rem", color: "#f8fafc" }}>Saved Students</h2>
            <button onClick={() => setIsSidebarOpen(false)} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", padding: 4 }}><X size={18} /></button>
          </div>
          <div style={{ padding: "12px 20px" }}>
            <input 
              value={adminSearch} 
              onChange={e => setAdminSearch(e.target.value)} 
              placeholder="Search by name or reg no..." 
              style={{ width: "100%", padding: "8px 12px", borderRadius: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", boxSizing: "border-box" }} 
            />
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px" }}>
            <button 
              onClick={() => {
                onSwitchStudent({ candidate_name: "New Student", reg_no: `TEMP-${Date.now()}`, sml: "", air: "", category: "OPEN", category_rank: "", gender: "Male" });
                setIsSidebarOpen(false);
              }}
              style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px dashed rgba(245,158,11,0.5)", background: "rgba(245,158,11,0.05)", color: "#f59e0b", cursor: "pointer", fontWeight: 700, marginBottom: 12 }}
            >
              + Create Empty Profile
            </button>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {adminSavedStudents.filter(s => 
                s.candidate_name?.toLowerCase().includes(adminSearch.toLowerCase()) || 
                s.reg_no?.toLowerCase().includes(adminSearch.toLowerCase())
              ).map(s => (
                <div key={s.reg_no} onClick={() => { onSwitchStudent(s); setIsSidebarOpen(false); }} style={{
                  padding: "10px 14px", borderRadius: 8, background: student?.reg_no === s.reg_no ? "rgba(139,92,246,0.15)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${student?.reg_no === s.reg_no ? "rgba(139,92,246,0.4)" : "rgba(255,255,255,0.06)"}`,
                  cursor: "pointer", transition: "all 0.2s"
                }}>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem", color: student?.reg_no === s.reg_no ? "#c4b5fd" : "#f8fafc" }}>{s.candidate_name}</div>
                  <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: 2 }}>AIR: {s.air || "N/A"} · SML: {s.sml || "N/A"} · {s.category}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ROOT — orchestrates Gate ↔ Predictor
═══════════════════════════════════════════════════════ */
export default function GuestPredictorGate({ isAdminMode = false }) {
  const [student,         setStudent]         = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Admin saved students state
  const [adminSavedStudents, setAdminSavedStudents] = useState([]);

  useEffect(() => {
    const initSession = async () => {
      try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.candidate_name && parsed?.reg_no && parsed?.air) {
            if (!isAdminMode) {
              try {
                const res = await api.post("/medical-predictor/verify-student", {
                  reg_no: parsed.reg_no,
                  air: parseInt(parsed.air),
                });
                if (res.data?.is_locked === false) {
                  sessionStorage.removeItem(SESSION_KEY);
                  setStudent(null);
                  setCheckingSession(false);
                  return;
                } else if (res.data?.is_locked) {
                  sessionStorage.setItem(SESSION_KEY, JSON.stringify(res.data));
                  setStudent(res.data);
                  setCheckingSession(false);
                  return;
                }
              } catch (e) {
                // Keep saved session if offline
              }
            }
            setStudent(parsed);
          }
        }
      } catch {}
      setCheckingSession(false);
    };

    initSession();
  }, [isAdminMode]);

  useEffect(() => {
    if (isAdminMode) {
      const loadAdminStudents = async () => {
        try {
          const res = await api.get("/medical-predictor/admin/guest-students");
          if (res.data?.students && res.data.students.length > 0) {
            const serverList = res.data.students;
            setAdminSavedStudents(serverList);
            setStudent(prev => {
              if (prev && prev.reg_no) {
                const found = serverList.find(s => s.reg_no === prev.reg_no);
                if (found) return found;
                return prev;
              }
              return serverList[0];
            });
            return;
          }
        } catch (err) {
          console.error("Failed to fetch server admin students, falling back to local storage", err);
        }

        try {
          const savedList = localStorage.getItem("admin_guest_students");
          let parsedList = [];
          if (savedList) parsedList = JSON.parse(savedList);
          setAdminSavedStudents(parsedList);

          setStudent(prev => {
            if (prev) return prev;
            if (parsedList.length > 0) return parsedList[0];
            return { candidate_name: "New Student", reg_no: `TEMP-${Date.now()}`, sml: "", air: "", category: "OPEN", category_rank: "", gender: "Male" };
          });
        } catch {}
      };

      loadAdminStudents();
    }
  }, [isAdminMode]);

  const handleBulkUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsName = wb.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wsName]);

        const formatted = data.map(row => ({
          reg_no: String(row["Reg No"] || row["Receipt No"] || row["reg_no"] || "").trim(),
          candidate_name: String(row["Candidate Name"] || row["Name"] || row["candidate_name"] || "").trim(),
          air: parseInt(row["AIR"] || row["Air"] || row["air"] || 0, 10),
          sml: parseInt(row["SML"] || row["Sml"] || row["sml"] || 0, 10) || null,
          category: String(row["Category"] || row["category"] || "OPEN").trim(),
          category_rank: parseInt(row["Category Rank"] || row["Cat Rank"] || row["category_rank"] || 0, 10) || null,
          gender: String(row["Gender"] || row["gender"] || "Male").trim(),
          phone_number: String(row["Phone"] || row["Mobile"] || row["phone_number"] || "").trim()
        })).filter(s => s.reg_no && s.air);

        if (formatted.length === 0) {
          toast.error("No valid student records found in file.");
          return;
        }

        const res = await api.post("/medical-predictor/admin/guest-students/bulk", { students: formatted });
        toast.success(res.data?.message || `Uploaded ${formatted.length} students.`);
        
        const refresh = await api.get("/medical-predictor/admin/guest-students");
        if (refresh.data?.students) {
          setAdminSavedStudents(refresh.data.students);
        }
      } catch (err) {
        toast.error("Failed to parse Excel file.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        "Receipt No": "MED 001",
        "Candidate Name": "John Doe",
        "AIR": 45000,
        "SML": 3200,
        "Category": "OBC",
        "Category Rank": 850,
        "Gender": "Male",
        "Phone": "9876543210"
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Student_Upload_Template.xlsx");
  };

  if (checkingSession && !isAdminMode) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0a0f1e", color: "#64748b"
      }}>
        <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "#f59e0b" }} />
      </div>
    );
  }

  if (!student && !isAdminMode) {
    return <VerificationGate onVerified={(st) => setStudent(st)} />;
  }

  return (
    <VerifiedPredictor
      student={student || { candidate_name: "Admin Tester", reg_no: "ADMIN-001", sml: 5000, air: 45000, category: "OPEN", category_rank: null, gender: "Male" }}
      isAdminMode={isAdminMode}
      adminSavedStudents={adminSavedStudents}
      onSwitchStudent={(s) => setStudent(s)}
      onBulkUpload={handleBulkUpload}
      onDownloadTemplate={handleDownloadTemplate}
      onUpdateStudent={(updated) => {
        setStudent(prev => ({ ...prev, ...updated }));
        if (!isAdminMode) {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...student, ...updated }));
        }
      }}
      onLogout={() => {
        sessionStorage.removeItem(SESSION_KEY);
        setStudent(null);
      }}
    />
  );
}
